/**
 * Pure math for the Alimony Analysis step (docx §4, §5, §7, §9). No React, so vitest covers it
 * directly.
 *
 * Nothing here is a guideline, a recommendation, or a presumptive amount. The quick fills are
 * analytical reference points the practitioner starts from — see docx §7 and §14.
 */

import { recalculateEngine as recalculate } from '../calcs/recalculateEngine';
import type { RecalculateOptions } from '../calcs/calcRuntimeContext';
import type {
    AlimonyPartyKey,
    AlimonyQuickFillKey,
    AlimonyScenarioChildSupportModel,
    AlimonyScenarioModel,
    CaseData,
} from '../types/case';
import { computeNetMonthlyIncome, type NetMonthlyIncomeResult } from './netMonthlyIncome';

const CENTS = 100;

/**
 * True when two amounts are level to the cent, allowing one cent between them: a gap of an odd
 * number of cents cannot be split evenly, so the closest two parties can get is a penny apart.
 * Compared as whole cents -- 6,370.00 minus 6,369.99 is 0.010000000000218 in floating point, which
 * a "<= 0.01" test wrongly calls more than a cent.
 */
export function levelToTheCent(a: number, b: number): boolean {
    return Math.abs(Math.round(a * CENTS) - Math.round(b * CENTS)) <= 1;
}

/** Rounds to cents. Mirrors `equalizeWithProperty.ts` `roundCurrency`. */
export function roundCurrency(value: number): number {
    return Math.round(value * CENTS) / CENTS;
}

export function otherPartyOf(party: AlimonyPartyKey): AlimonyPartyKey {
    return party === 'partyA' ? 'partyB' : 'partyA';
}

/**
 * Returns a deep clone with every INTER-PARTY spousal support removed from both parties.
 *
 * This is the whole reason Section 1 cannot read `worksheet1` as-is: gross income already nets any
 * spousal support entered on the Income screen (`worksheet1Calcs.ts`:
 * `total = sourcesAndAmounts + spousalSupportReceivedYearly - |spousalSupportPaidYearly|`), so a
 * case that already carries an amount would count it once in the baseline and again in the
 * scenario. docx §4.
 *
 * Third-party alimony is deliberately KEPT — it is real outside income, not the amount under
 * negotiation.
 */
export function stripInterPartySpousalSupport(caseData: CaseData): CaseData {
    const clone: CaseData = JSON.parse(JSON.stringify(caseData));

    for (const key of ['partyA', 'partyB'] as const) {
        const party = clone.parties?.[key];
        if (!party?.spousalSupports) continue;
        party.spousalSupports = party.spousalSupports.filter((s) => s?.otherParty !== 'party');
    }

    return clone;
}

/**
 * Each party's monthly income after federal tax, state tax and FICA, but BEFORE any spousal support
 * currently on the Income screen. docx §4.
 *
 * `options` is how the server runs this: the API executes this same function through Jint and has
 * to pass the server runtime and its lookups, because there is no browser to fetch them.
 *
 * Runs the real calc engine against the stripped case rather than adjusting the existing worksheet
 * figures by hand — taxes are not linear in income, so backing an amount out arithmetically would
 * give the wrong federal and state liabilities. docx §14.
 */
export function computeBaseline(caseData: CaseData, options?: RecalculateOptions): NetMonthlyIncomeResult {
    const stripped = stripInterPartySpousalSupport(caseData);
    return computeNetMonthlyIncome(recalculate(stripped, options));
}

/**
 * True when the alimony is more than the payor's net monthly income: they cannot pay out more than
 * they have coming in, so the scenario is not a valid situation. Compared to the cent.
 */
export function alimonyExceedsPayorNetIncome(
    baseline: NetMonthlyIncomeResult,
    payor: AlimonyPartyKey,
    amount: number,
): boolean {
    return roundCurrency(Math.abs(amount || 0)) > roundCurrency(baseline[payor].netMonthly);
}

/**
 * The roles a party plays in a scenario, for the line under their name: always their alimony role,
 * and their child support role too when the scenario has a child support figure with a direction.
 * "Alimony Recipient; Child Support Payor".
 */
export function partyRoles(
    party: AlimonyPartyKey,
    payor: AlimonyPartyKey,
    childSupport: AlimonyScenarioChildSupportModel | null | undefined,
): string {
    const roles = [party === payor ? 'Alimony Payor' : 'Alimony Recipient'];
    if (childSupport?.monthlyAmount && childSupport.payor && childSupport.payee) {
        roles.push(party === childSupport.payor ? 'Child Support Payor' : 'Child Support Recipient');
    }
    return roles.join('; ');
}

/** Absolute monthly difference between the two parties' pre-alimony net incomes. docx §4. */
export function netIncomeDifference(baseline: NetMonthlyIncomeResult): number {
    return roundCurrency(Math.abs(baseline.partyA.netMonthly - baseline.partyB.netMonthly));
}

/**
 * What each party's net monthly income would be if alimony brought the two level: the midpoint of
 * the pre-alimony net incomes. The "Equalize Net Income" quick fill is the payment that gets there.
 */
export function equalizedNetIncome(baseline: NetMonthlyIncomeResult): number {
    return roundCurrency((baseline.partyA.netMonthly + baseline.partyB.netMonthly) / 2);
}

/** The party with the higher pre-alimony net income; null when they are equal. */
export function higherEarner(baseline: NetMonthlyIncomeResult): AlimonyPartyKey | null {
    const diff = roundCurrency(baseline.partyA.netMonthly - baseline.partyB.netMonthly);
    if (diff === 0) return null;
    return diff > 0 ? 'partyA' : 'partyB';
}

/**
 * The scenario amount as a percentage of the parties' net-income difference. Informational only.
 * docx §9. Returns null when the difference is zero (the percentage is undefined, not 0%).
 */
export function percentOfDifference(amount: number, difference: number): number | null {
    if (!difference) return null;
    return (Math.abs(amount) / Math.abs(difference)) * 100;
}

export interface QuickFillInputs {
    payorNetMonthly: number;
    recipientNetMonthly: number;
    payorExpenses: number;
    recipientExpenses: number;
    /**
     * This scenario's child support, signed from each party's point of view: negative for whoever
     * owes it, positive for whoever receives it, 0 when the scenario leaves it out.
     *
     * A recipient who is also collecting child support is not as short as their income alone
     * suggests, and an amount that ignores that overshoots -- which is the whole reason these are
     * here rather than being worked out by hand.
     */
    payorChildSupport: number;
    recipientChildSupport: number;
}

/**
 * Brings the recipient from a monthly deficit to $0, using net income, anticipated expenses and
 * this scenario's child support. A need-based reference point only. docx §7.
 *
 * Clamped at 0: a recipient who already has a surplus produces no amount, never a negative one.
 */
export function meetRecipientsShortfall(inputs: QuickFillInputs): number {
    const available = inputs.recipientNetMonthly + inputs.recipientChildSupport;
    return roundCurrency(Math.max(0, inputs.recipientExpenses - available));
}

/**
 * Equalises the parties' net monthly incomes, before expenses and before scenario-specific child
 * support. docx §7.
 *
 * Child support stays out of this one by definition -- it is the income comparison, not the
 * household one. The other two quick fills fold it in.
 */
export function equalizeNetIncome(inputs: QuickFillInputs): number {
    return roundCurrency(Math.max(0, (inputs.payorNetMonthly - inputs.recipientNetMonthly) / 2));
}

/**
 * Equalises the parties' monthly surplus after anticipated expenses and this scenario's child
 * support. docx §7.
 */
export function equalizeSurplusAfterExpenses(inputs: QuickFillInputs): number {
    const payorSurplus = inputs.payorNetMonthly + inputs.payorChildSupport - inputs.payorExpenses;
    const recipientSurplus = inputs.recipientNetMonthly + inputs.recipientChildSupport - inputs.recipientExpenses;
    return roundCurrency(Math.max(0, (payorSurplus - recipientSurplus) / 2));
}

export type QuickFillKey = AlimonyQuickFillKey;

/**
 * Labels are fixed here so the UI cannot accidentally describe a quick fill as a guideline or a
 * recommendation. docx §14.
 */
export const QUICK_FILL_LABELS: Record<QuickFillKey, string> = {
    meetShortfall: "Meet Recipient's Shortfall",
    equalizeNetIncome: 'Equalize Net Income',
    equalizeSurplus: 'Equalize Surplus After Expenses',
};

export function runQuickFill(key: QuickFillKey, inputs: QuickFillInputs): number {
    switch (key) {
        case 'meetShortfall': return meetRecipientsShortfall(inputs);
        case 'equalizeNetIncome': return equalizeNetIncome(inputs);
        case 'equalizeSurplus': return equalizeSurplusAfterExpenses(inputs);
    }
}

/**
 * Builds the quick-fill inputs for one scenario, honouring its selected direction. docx §7.
 *
 * `childSupport` is the figure this scenario actually uses -- null when the case has no children,
 * when the scenario turns child support off, or when the saved figure was calculated for a
 * different alimony amount. Passing a stale figure here would be the same mistake the arithmetic
 * refuses to make: an amount worked out against a child-support number that no longer applies.
 */
export function quickFillInputsFor(
    baseline: NetMonthlyIncomeResult,
    expenses: Record<AlimonyPartyKey, number>,
    payor: AlimonyPartyKey,
    childSupport?: AlimonyScenarioChildSupportModel | null,
): QuickFillInputs {
    const recipient = otherPartyOf(payor);
    return {
        payorNetMonthly: baseline[payor].netMonthly,
        recipientNetMonthly: baseline[recipient].netMonthly,
        payorExpenses: expenses[payor],
        recipientExpenses: expenses[recipient],
        payorChildSupport: childSupportFor(childSupport, payor),
        recipientChildSupport: childSupportFor(childSupport, recipient),
    };
}

/** One party's share of a scenario's child support: positive to the payee, negative from the payor. */
function childSupportFor(
    childSupport: AlimonyScenarioChildSupportModel | null | undefined,
    party: AlimonyPartyKey,
): number {
    const monthly = childSupport?.monthlyAmount ?? 0;
    if (!monthly) return 0;
    if (childSupport?.payee === party) return monthly;
    if (childSupport?.payor === party) return -monthly;
    return 0;
}

/** One line of a scenario's per-party arithmetic, in display order. */
export interface ScenarioLine {
    key: 'netMonthlyIncome' | 'alimony' | 'netAfterAlimony' | 'childSupport' | 'expenses' | 'surplus';
    label: string;
    /** Signed. `netAfterAlimony` and `surplus` are running subtotals. */
    amount: number;
}

export interface ScenarioPartyArithmetic {
    party: AlimonyPartyKey;
    isAlimonyPayor: boolean;
    netMonthlyIncome: number;
    /** Negative for the payor, positive for the recipient. */
    alimony: number;
    netAfterAlimony: number;
    /**
     * Net income plus child support less expenses, before any alimony: negative is a shortfall.
     * The position Meet Shortfall brings to zero and Equalize Surplus balances between the parties.
     */
    surplusBeforeAlimony: number;
    /** Negative for the child-support payor, positive for the payee, 0 when not applicable. */
    childSupport: number;
    /** Negative. */
    expenses: number;
    surplus: number;
    lines: ScenarioLine[];
}

export interface ScenarioArithmetic {
    payor: AlimonyPartyKey;
    recipient: AlimonyPartyKey;
    amount: number;
    partyA: ScenarioPartyArithmetic;
    partyB: ScenarioPartyArithmetic;
}

/**
 * Both parties' arithmetic for one scenario. docx §8.
 *
 * The child-support payor is NOT assumed to be the alimony payor — the direction comes from the
 * scenario's own calculated result, and the amount is subtracted from whichever party owes it.
 * Passing `childSupport: null` produces the no-children shape (docx §11).
 */
export function scenarioArithmetic(
    baseline: NetMonthlyIncomeResult,
    expenses: Record<AlimonyPartyKey, number>,
    payor: AlimonyPartyKey,
    amount: number,
    childSupport: AlimonyScenarioChildSupportModel | null | undefined,
): ScenarioArithmetic {
    const recipient = otherPartyOf(payor);
    const absAmount = Math.abs(amount || 0);

    // Both parties' net income once alimony has moved: level means the amount equalizes them.
    const netAfter = (party: AlimonyPartyKey) =>
        baseline[party].netMonthly + (party === payor ? -absAmount : absAmount);
    // Within a cent counts: an odd-cent gap cannot be split evenly, so one side is always a penny over.
    const netIncomesLevel = levelToTheCent(netAfter('partyA'), netAfter('partyB'));

    // Each party's monthly surplus, worked out up front so the label can say when the two are level.
    const childSupportOf = (party: AlimonyPartyKey) => {
        if (!childSupport || !childSupport.monthlyAmount) return 0;
        const abs = Math.abs(childSupport.monthlyAmount);
        return childSupport.payor === party ? -abs : childSupport.payee === party ? abs : 0;
    };
    const surplusOf = (party: AlimonyPartyKey) =>
        baseline[party].netMonthly + childSupportOf(party) - Math.abs(expenses[party] || 0)
        + (party === payor ? -absAmount : absAmount);
    const surplusesLevel = levelToTheCent(surplusOf('partyA'), surplusOf('partyB'));

    const build = (party: AlimonyPartyKey): ScenarioPartyArithmetic => {
        const isAlimonyPayor = party === payor;
        const netMonthlyIncome = baseline[party].netMonthly;
        const alimony = isAlimonyPayor ? -absAmount : absAmount;
        const netAfterAlimony = netMonthlyIncome + alimony;

        let childSupportAmount = 0;
        if (childSupport && childSupport.monthlyAmount) {
            const absChildSupport = Math.abs(childSupport.monthlyAmount);
            if (childSupport.payor === party) childSupportAmount = -absChildSupport;
            else if (childSupport.payee === party) childSupportAmount = absChildSupport;
        }

        const partyExpenses = -Math.abs(expenses[party] || 0);
        // Whole cents, and never a signed zero: a figure that is nothing to the cent is a surplus of
        // $0.00, not a deficit of "- $0.00" left by floating-point noise (a shortfall of 0.0000001).
        const wholeCents = (value: number) => {
            const rounded = roundCurrency(value);
            return rounded === 0 ? 0 : rounded;
        };
        const surplusBeforeAlimony = wholeCents(netMonthlyIncome + childSupportAmount + partyExpenses);
        const surplus = wholeCents(netMonthlyIncome + childSupportAmount + partyExpenses + alimony);

        // Ordered so each subtotal is the sum of the rows above it: net income, then alimony, then
        // what is left after alimony. When both parties land on the same figure that is the
        // equalized net income, and the row says so.
        const lines: ScenarioLine[] = [
            { key: 'netMonthlyIncome', label: 'Net Monthly Income', amount: netMonthlyIncome },
            { key: 'alimony', label: isAlimonyPayor ? 'Less Alimony' : 'Plus Alimony', amount: alimony },
            {
                key: 'netAfterAlimony',
                label: netIncomesLevel ? 'Equalized Net Income' : 'Net Income After Alimony',
                amount: netAfterAlimony,
            },
        ];

        if (childSupportAmount !== 0) {
            lines.push({
                key: 'childSupport',
                label: childSupportAmount < 0 ? 'Less Child Support' : 'Plus Child Support',
                amount: childSupportAmount,
            });
        }

        lines.push({ key: 'expenses', label: 'Less Expenses', amount: partyExpenses });
        lines.push({
            key: 'surplus',
            // "Equalized" when the two parties come out level to the cent, like the net income row.
            // Just the one that applies: a surplus (zero included) or a deficit.
            label: `${surplusesLevel ? 'Equalized ' : ''}${surplus < 0 ? 'Monthly Deficit' : 'Monthly Surplus'}`,
            amount: surplus,
        });

        return {
            party,
            isAlimonyPayor,
            netMonthlyIncome,
            alimony,
            netAfterAlimony,
            surplusBeforeAlimony,
            childSupport: childSupportAmount,
            expenses: partyExpenses,
            surplus,
            lines,
        };
    };

    return { payor, recipient, amount: absAmount, partyA: build('partyA'), partyB: build('partyB') };
}

/**
 * True when a scenario's child-support preview no longer matches its alimony amount OR its
 * direction.
 *
 * The direction half matters as much as the amount: flipping the payor select leaves the amount
 * untouched, so an amount-only check would keep a preview computed for the opposite direction and
 * feed it into both parties' arithmetic. docx §7 requires that a stale figure is never silently
 * presented as current.
 *
 * Derived rather than stored: a persisted boolean would drift across a reload.
 */
export function isChildSupportStale(scenario: AlimonyScenarioModel, inputsFingerprint?: string): boolean {
    const preview = scenario.childSupport;
    if (!preview) return false;

    const calculatedFor = Math.abs(preview.calculatedForAlimonyAmount ?? 0);
    if (roundCurrency(calculatedFor) !== roundCurrency(Math.abs(scenario.monthlyAmount ?? 0))) return true;

    // A preview written before calculatedForPayor existed has no recorded direction; treat it as
    // stale rather than assuming it matches.
    if ((preview.calculatedForPayor ?? null) !== (scenario.payor ?? null)) return true;

    // Any other change that feeds child support -- income, taxes, children, spousal support. Only
    // checked when the caller knows the case's current fingerprint. A preview with none recorded
    // predates the check and is recalculated once.
    if (inputsFingerprint !== undefined && preview.calculatedForInputs !== inputsFingerprint) return true;
    return false;
}

/**
 * What a scenario's quick fill amount is worked out from: the case inputs, both parties'
 * expenses, the direction, and whether child support counts. Stored with the amount; if it no
 * longer matches, the amount is out of date.
 */
export function quickFillBasisFor(
    inputsFingerprint: string,
    expenses: Record<AlimonyPartyKey, number>,
    payor: AlimonyPartyKey | undefined,
    considerChildSupport: boolean | undefined,
): string {
    return [inputsFingerprint, expenses.partyA ?? 0, expenses.partyB ?? 0, payor ?? '', considerChildSupport !== false].join('|');
}

/** The case-inputs part of a stored basis. */
export function quickFillBasisInputs(basis: string | undefined): string {
    return (basis ?? '').split('|')[0] ?? '';
}

/** True for a scenario still following a quick fill whose amount was worked out from something else. */
export function quickFillNeedsResolve(
    scenario: AlimonyScenarioModel,
    inputsFingerprint: string,
    expenses: Record<AlimonyPartyKey, number>,
): boolean {
    if (!scenario.quickFill || !scenario.payor) return false;
    return scenario.quickFillBasis
        !== quickFillBasisFor(inputsFingerprint, expenses, scenario.payor, scenario.considerChildSupport);
}

/**
 * True while nobody has done anything to a scenario: no amount entered, no notes, no child
 * support figure. Such a scenario has no direction of its own to protect, so it follows the
 * default. Choosing a payor on it counts as touching it (the page stores an amount of 0 with it).
 */
export function isScenarioUntouched(scenario: AlimonyScenarioModel): boolean {
    return scenario.monthlyAmount === undefined
        && !scenario.notes?.trim()
        && !scenario.childSupport;
}

/**
 * The payor a scenario is shown and worked with. An untouched scenario follows the default, so a
 * change to the incomes carries into it; one the user has worked on keeps
 * the direction it was given.
 */
export function effectiveScenarioPayor(
    scenario: AlimonyScenarioModel,
    defaultPayor: AlimonyPartyKey,
): AlimonyPartyKey {
    if (isScenarioUntouched(scenario)) return defaultPayor;
    return scenario.payor ?? defaultPayor;
}

/** Sum of every expense in the case's budget — the Section 2 "pull client's budget" source. docx §5. */
/**
 * Whether a scenario's child support figure needs (re)calculating for the amount it currently
 * holds.
 *
 * The whole automatic-recalculation rule, in one predicate, so it can be reasoned about and tested
 * rather than living inside an effect. Two cases are easy to conflate and behave differently:
 * a scenario with NO figure yet is not "stale" -- isChildSupportStale returns false for it,
 * because there is nothing to be out of date -- but it does still need calculating.
 *
 * A zero amount with no figure is deliberately left alone. Every newly added card starts at zero,
 * and running the calc engine for each one the moment it appears spends real work to tell the user
 * nothing they asked about yet. One that already has a figure is kept current like any other.
 */
export function needsChildSupportRecalc(scenario: AlimonyScenarioModel, inputsFingerprint?: string): boolean {
    if (scenario.considerChildSupport === false) return false;
    if (!scenario.childSupport) return Math.abs(scenario.monthlyAmount ?? 0) > 0;
    return isChildSupportStale(scenario, inputsFingerprint);
}

/**
 * Whether a calculated child-support figure can be shown and used in the arithmetic.
 *
 * A case with no income entered divides by a zero combined income and comes back non-finite.
 * Rendering that produces "$ ∞" in the child-support box and in both parties' surplus. It was
 * always possible, but it used to take a deliberate button press to reach; once the calculation
 * runs on its own it appears the moment anyone types an amount, so it has to be rejected at the
 * source rather than formatted.
 *
 * An absent amount is treated as usable: that is the engine reporting no child support for this
 * scenario, which is a real answer, not a broken one.
 */
export function isUsableChildSupportAmount(amount: number | undefined): boolean {
    return amount === undefined || Number.isFinite(amount);
}

export function budgetMonthlyTotal(caseData: CaseData): number {
    return (caseData.financial?.expenseGroups ?? []).reduce((sum, group) => (
        sum + (group.expenses ?? []).reduce((groupSum, expense) => groupSum + (expense.amount || 0), 0)
    ), 0);
}
